import type {
  CampaignBannerMerchandising,
  CategoryBrandingMerchandising,
  CollectionMerchandising,
  CollectionUUID,
  CompactCampaignBannerMerchandising,
  Recipe,
  RecipeUUID,
} from '@library/menu-service'
import type {
  GradientInfoHeaderMerchandising,
  WaveLinkHeaderMerchandising,
} from '@library/menu-service/internal/types'

import { useCollection } from 'hooks/menu-collections'
import { useRecipes } from 'hooks/menu-recipes/useRecipes'

import {
  toCampaignBannerContentBlock,
  toCategoryBrandingContentBlock,
  toCompactCampaignBannerContentBlock,
  toGradientInfoHeaderContentBlock,
  toWaveLinkHeaderContentBlock,
} from './adapters'
import type { MerchandisingHeaderContentBlock } from './contentBlockTypes'

const isSupportedMerchandisingHeader = (
  merchandising: CollectionMerchandising,
): merchandising is
  | CategoryBrandingMerchandising
  | CampaignBannerMerchandising
  | CompactCampaignBannerMerchandising
  | WaveLinkHeaderMerchandising
  | GradientInfoHeaderMerchandising =>
  merchandising.type === 'category-branding' ||
  merchandising.type === 'campaign-banner' ||
  merchandising.type === 'compact-campaign-banner' ||
  merchandising.type === 'wave-link-header' ||
  merchandising.type === 'gradient-info-header'

const getMerchandisingHeaderRecipeIds = (
  merchandisingHeader:
    | CategoryBrandingMerchandising
    | CampaignBannerMerchandising
    | CompactCampaignBannerMerchandising
    | WaveLinkHeaderMerchandising
    | GradientInfoHeaderMerchandising
    | undefined,
): RecipeUUID[] => {
  if (!merchandisingHeader) {
    return []
  }

  if (merchandisingHeader.type === 'campaign-banner') {
    return merchandisingHeader.items
      .filter(
        (item): item is Extract<CampaignBannerMerchandising['items'][number], { type: 'recipe' }> =>
          item.type === 'recipe',
      )
      .map((item) => item.id)
  }

  if (merchandisingHeader.type === 'compact-campaign-banner') {
    return merchandisingHeader.items.map((item) => item.id)
  }

  return []
}

export const useMerchandisingHeader = (
  collectionId: CollectionUUID | null,
): MerchandisingHeaderContentBlock | null => {
  const collection = useCollection(collectionId)

  const merchandisingHeader = collection?.merchandising?.find(isSupportedMerchandisingHeader)
  const recipes = useRecipes(getMerchandisingHeaderRecipeIds(merchandisingHeader))

  if (!merchandisingHeader) {
    return null
  }

  const recipeById = Object.fromEntries(
    recipes.flatMap((recipe) => (recipe ? [[recipe.id, recipe]] : [])),
  ) as Record<string, Recipe>

  const getRecipeById = (id: string) => recipeById[id] ?? null

  switch (merchandisingHeader.type) {
    case 'category-branding':
      return toCategoryBrandingContentBlock(merchandisingHeader)
    case 'wave-link-header':
      return toWaveLinkHeaderContentBlock(merchandisingHeader)
    case 'gradient-info-header':
      return toGradientInfoHeaderContentBlock(merchandisingHeader)
    case 'campaign-banner':
      return toCampaignBannerContentBlock(merchandisingHeader, getRecipeById)
    case 'compact-campaign-banner':
      return toCompactCampaignBannerContentBlock(merchandisingHeader, getRecipeById)
    default:
      return null
  }
}
